// Workflow schedule presets (WS-D) — the friendly preset → cron map.
//
// A presentation projection enum (CONTEXT.md → "Client Application
// Architecture"): the projection layer cannot import the ontology, but it may
// hold its own enums. The web (WS-G) imports this map to render the schedule
// picker; the server controller compiles a `(presetKey, cron?)` pair to the
// stored cron via `compilePreset`. The stored value is always a cron + the
// `presetKey` that produced it (or `raw`, where the caller supplies the cron).
//
// Pure data + a pure compile helper — no nest / cron library here either.

import { z } from 'zod';

/**
 * The friendly presets and the cron each compiles to. `raw` is the advanced
 * escape hatch: the caller supplies the cron verbatim (it carries no fixed cron
 * here). Times are firm-local (the server cron runs in the host timezone).
 */
export const WORKFLOW_SCHEDULE_PRESETS = {
    /** Every hour, on the hour. */
    hourly: '0 * * * *',
    /** Every day at 08:00. */
    daily_0800: '0 8 * * *',
    /** Every weekday (Mon–Fri) at 08:00. */
    weekdays_0800: '0 8 * * 1-5',
    /** Every Monday at 08:00. */
    weekly_mon_0800: '0 8 * * 1',
    /** The 1st of each month at 08:00. */
    monthly_1_0800: '0 8 1 * *',
    /** Advanced — the caller supplies the cron expression. */
    raw: null,
} as const satisfies Record<string, string | null>;

export type WorkflowSchedulePresetKey = keyof typeof WORKFLOW_SCHEDULE_PRESETS;

/** The closed set of preset keys, for the contract body + the web picker. */
export const WORKFLOW_SCHEDULE_PRESET_KEYS = Object.keys(
    WORKFLOW_SCHEDULE_PRESETS,
) as WorkflowSchedulePresetKey[];

/** True when `key` is a known preset key. */
export function isWorkflowSchedulePresetKey(key: string): key is WorkflowSchedulePresetKey {
    return key in WORKFLOW_SCHEDULE_PRESETS;
}

/**
 * Compile a `(presetKey, cron?)` pair to the cron to store.
 *   - a fixed preset → its mapped cron (the `cron` arg is ignored);
 *   - `raw` → the supplied `cron` (required — throws when absent/blank);
 *   - an unknown preset key → throws.
 * Pure: the structural cron validation lives in the SetWorkflowSchedule Action.
 */
export function compilePreset(presetKey: string, cron?: string): string {
    if (!isWorkflowSchedulePresetKey(presetKey)) {
        throw new Error(`Unknown schedule preset "${presetKey}".`);
    }
    if (presetKey === 'raw') {
        const trimmed = (cron ?? '').trim();
        if (trimmed.length === 0) {
            throw new Error('A raw schedule requires a cron expression.');
        }
        return trimmed;
    }
    // A fixed preset always maps to a non-null cron.
    return WORKFLOW_SCHEDULE_PRESETS[presetKey] as string;
}

/** The schedule shape returned by `getSchedule` (null when unscheduled). */
export const workflowScheduleDtoSchema = z.object({
    cron: z.string(),
    presetKey: z.string().nullable(),
    enabled: z.boolean(),
    lastRunAt: z.string().nullable(),
});
export type WorkflowScheduleDto = z.infer<typeof workflowScheduleDtoSchema>;
